import {
  Alert,
  type AlertRootProps,
  HStack,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import React from 'react'

type ExtensionStatusConfiguration = {
  /**
   * Title of the status message.
   */
  title: string
  /**
   * Fallback description if the status message is not provided.
   */
  description: string
  /**
   * Status of the alert.
   */
  status: AlertRootProps['status']
  /**
   * Show the icon in the alert.
   */
  showIcon?: boolean
  /**
   * Show the spinner in the alert.
   */
  showSpinner?: boolean
  /**
   * Color palette of the spinner.
   */
  spinnerColor?:
    | 'gray'
    | 'red'
    | 'orange'
    | 'yellow'
    | 'green'
    | 'teal'
    | 'blue'
    | 'cyan'
    | 'purple'
    | 'pink'
}

export enum ExtensionStatusState {
  CONFIGURED = 'CONFIGURED',
  RUNNING = 'RUNNING',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  DELETING = 'DELETING',
  INACTIVE = 'INACTIVE',
}

const INTEGRATION_STATE_CONFIGURATION: Record<
  ExtensionStatusState,
  ExtensionStatusConfiguration
> = {
  CONFIGURED: {
    title: 'Extension is configured',
    description: 'The extension has been added to the configuration.',
    status: 'success',
    showIcon: true,
  },
  RUNNING: {
    title: 'Extension Active',
    description: 'The extension is ready for your use-case.',
    status: 'success',
    showIcon: true,
  },
  INACTIVE: {
    title: 'Extension is inactive',
    description: 'No configuration has been set for this extension.',
    status: 'info',
    showIcon: true,
  },
  ERROR: {
    title: 'Extension Error',
    description: 'An error occurred while setting up the extension.',
    status: 'error',
    showIcon: true,
  },
  PENDING: {
    title: 'Extension is loading',
    description: 'We are currently applying your configuration changes.',
    status: 'info',
    showSpinner: true,
    spinnerColor: 'blue',
  },
  DELETING: {
    title: 'Extension is being removed',
    description: 'We are currently removing your configuration.',
    status: 'warning',
    showSpinner: true,
    spinnerColor: 'orange',
  },
}

type ExtensionStatusProps = {
  status: ExtensionStatusState
  actions?: React.ReactNode
}

export function ExtensionStatus({ actions, status }: ExtensionStatusProps) {
  const extensionState = React.useMemo(() => {
    if (!status) {
      return {
        title: 'Unknown Extension Status',
        description: 'Please check your network connection and try again.',
        status: 'error',
        showIcon: true,
      } satisfies ExtensionStatusConfiguration
    }

    return INTEGRATION_STATE_CONFIGURATION[status]
  }, [status])

  return (
    extensionState?.status && (
      <Alert.Root
        status={extensionState?.status}
        mt={-2}
        role="alert"
        data-status={extensionState?.status}
      >
        <HStack gap={4}>
          {extensionState.showIcon && (
            <Alert.Indicator mr={0} w={22} h={22} data-testid="alert-icon" />
          )}
          {extensionState.showSpinner && (
            <Spinner colorPalette={extensionState.spinnerColor} size="md" />
          )}

          <VStack alignItems="start" gap={4}>
            <VStack alignItems="start" gap={0}>
              <Alert.Title>{extensionState.title}</Alert.Title>
              <Alert.Description>
                {extensionState.description}
              </Alert.Description>
            </VStack>
            {actions}
          </VStack>
        </HStack>
      </Alert.Root>
    )
  )
}
